import type { CSSProperties, FC, ReactNode } from 'react'
import type { ConstructionMilestone } from '@/types'

import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'

interface ConstructionTimelineProps {
    milestones: Array<ConstructionMilestone>
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const monthIndex = (date: string): number => {
    const match = /^(\d{4})-(\d{2})/.exec(date)

    return match ? Number(match[1]) * 12 + Number(match[2]) - 1 : 0
}

const formatMonth = (date: string): string => {
    const match = /^(\d{4})-(\d{2})/.exec(date)

    return match ? `${MONTHS[Number(match[2]) - 1]} ${match[1]}` : date
}

const STATUS_LABEL: Record<ConstructionMilestone['status'], string> = {
    done: 'Done',
    current: 'In progress',
    upcoming: 'Upcoming'
}

/**
 * Survey to handover on one horizontal bar, each milestone placed by its month. Upcoming milestones
 * are hollow and joined by a dashed line; the current one is blue.
 */
export const ConstructionTimeline: FC<ConstructionTimelineProps> = ({ milestones }): ReactNode => {
    const [ref, inView] = useInView<HTMLDivElement>({ rootMargin: '0px 0px -15% 0px' })

    if (milestones.length === 0) {
        return (
            <p className='hud border border-hairline px-4 py-8 text-center text-mute'>
                The construction programme is being confirmed.
            </p>
        )
    }

    const first = monthIndex(milestones[0].date)
    const last = monthIndex(milestones[milestones.length - 1].date)
    const span = Math.max(1, last - first)
    const position = (date: string): number => ((monthIndex(date) - first) / span) * 100
    const lastDone = milestones.reduce((index, milestone, i) => (milestone.status !== 'upcoming' ? i : index), 0)
    const doneTo = position(milestones[lastDone].date)
    const months = last - first

    return (
        <div ref={ref} data-reveal={inView ? 'in' : 'pre'}>
            <ol className='grid gap-0 border-t border-hairline desk:hidden'>
                {milestones.map(milestone => (
                    <li key={milestone.label} className='flex items-center gap-4 border-b border-hairline py-4'>
                        <span
                            className={cn(
                                'size-2.5 shrink-0 border',
                                milestone.status === 'done' && 'border-ink bg-ink',
                                milestone.status === 'current' && 'border-signal bg-signal',
                                milestone.status === 'upcoming' && 'border-mute bg-background'
                            )}
                            aria-hidden='true'
                        />

                        <span className='hud flex-1 text-ink'>
                            {milestone.label}
                        </span>

                        <span className='hud text-mute'>
                            <time dateTime={milestone.date}>
                                {formatMonth(milestone.date)}
                            </time>
                            {' / '}
                            {STATUS_LABEL[milestone.status]}
                        </span>
                    </li>
                ))}
            </ol>

            <div className='hidden desk:block'>
                <div className='relative mx-16 h-36'>
                    <span className='absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-hairline' aria-hidden='true' />
                    <span className='draw-x absolute left-0 top-1/2 h-[2px] -translate-y-1/2 bg-ink' style={{ width: `${doneTo}%` }} aria-hidden='true' />

                    {doneTo < 100 && (
                        <span className='absolute top-1/2 h-px -translate-y-1/2 border-t border-dashed border-mute' style={{ left: `${doneTo}%`, right: 0 }} aria-hidden='true' />
                    )}

                    <ol>
                        {milestones.map((milestone, index) => {
                            const above = index % 2 === 0

                            return (
                                <li
                                    key={milestone.label}
                                    className='reveal-cell absolute top-1/2 -translate-x-1/2'
                                    style={{ left: `${position(milestone.date)}%`, '--delay': `${300 + index * 160}ms` } as CSSProperties}
                                >
                                    <span
                                        className={cn(
                                            'absolute left-1/2 top-0 block size-3 -translate-x-1/2 -translate-y-1/2 border',
                                            milestone.status === 'done' && 'border-ink bg-ink',
                                            milestone.status === 'current' && 'border-signal bg-signal',
                                            milestone.status === 'upcoming' && 'border-mute bg-background'
                                        )}
                                        aria-hidden='true'
                                    />

                                    <span className={cn('absolute left-1/2 flex w-40 -translate-x-1/2 flex-col items-center text-center', above ? 'bottom-4' : 'top-4')}>
                                        <span className={cn('hud', milestone.status === 'upcoming' ? 'text-mute' : 'text-ink')}>
                                            {milestone.label}
                                        </span>

                                        <span className={cn('hud', milestone.status === 'current' ? 'text-signal' : 'text-mute')}>
                                            <time dateTime={milestone.date}>
                                                {formatMonth(milestone.date)}
                                            </time>
                                            {milestone.status !== 'done' && ` / ${STATUS_LABEL[milestone.status]}`}
                                        </span>
                                    </span>
                                </li>
                            )
                        })}
                    </ol>
                </div>
            </div>

            <p className='hud mt-4 text-mute'>
                {formatMonth(milestones[0].date)} to {formatMonth(milestones[milestones.length - 1].date)} / {months} months
            </p>
        </div>
    )
}
